import * as React from "react";
import {useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {useNavigate, useSearchParams} from "react-router";
import {passwordResetRequest, passwordResetVerifiedRequest, verifyEmail} from "../apis/junkyard";
import {EmailVerificationRequest, PasswordResetVerifiedRequest} from "../utils/types";
import Button from "@mui/material/Button";
import AppTheme from "../themes/AppTheme";
import {JunkyardCard} from "./JunkyardCard";
import {SitemarkIcon} from "./CustomIcons";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";
import SignInCard from "./SignInCard";
import JunkyardCardContainer from "./JunkyardCardContainer";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";

interface EmailVerifyProps {
    disableCustomTheme?: boolean;

}

export const EmailVerify: React.FC<EmailVerifyProps> = (props: {}) => {
    const [searchParams] = useSearchParams()
    let [verified, setVerified] = useState<boolean>(false);
    let [status, setStatus] = useState<string>("Verifying...")
    const [email, setEmail] = React.useState<string | undefined>(undefined);
    const [passwordError, setPasswordError] = React.useState(false);
    const [enablePasswordReset, setEnablePasswordReset] = useState<boolean>(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState<string>('');
    const [passwordResetSuccess, setPasswordResetSuccessful] = React.useState<boolean>(false);
    const navigate = useNavigate(); // Add this at the component level

    const navigateToLogin = () => {
        navigate("/login");
    }

    const submitPasswordReset = async (event: React.FormEvent<HTMLFormElement>) => {

        // prevent browser from reloading the page
        event.preventDefault();
        setPasswordResetSuccessful(false);

        if (!email) {
            setPasswordError(true);
            setPasswordErrorMessage("No email")
            return;
        }

        const code = searchParams.get("code");

        if (!code) {
            setPasswordError(true);
            setPasswordErrorMessage("No verification code")
            return;
        }

        const data = new FormData(event.currentTarget);
        let password = data.get("password")?.toString() ?? "";
        let passwordDup = data.get("passwordDup")?.toString() ?? "";


        if (password !== passwordDup) {
            setPasswordError(true)
            setPasswordErrorMessage("The passwords do not match")
            return;
        }

        if (!password) {
            setPasswordError(true);
            setPasswordErrorMessage("No password provided")
            return;
        }

        if (password && password.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage("Password must be at least 6 characters.")
            return;
        }

        try {
            const rDetail = {"email": email, "new_password": password, "verification_code": code} as PasswordResetVerifiedRequest;
            const resp = await passwordResetVerifiedRequest(rDetail);
            if (resp.success) {
                setPasswordResetSuccessful(true);
                return;
            }

        } catch (error) {
            console.error(error)
        }
        setPasswordResetSuccessful(false);
    }

    useEffect(() => {

        const doVerify = async () => {
            const email = searchParams.get("email");
            const code = searchParams.get("code");
            const resetpass = parseInt(searchParams.get("resetpass") ?? "") || undefined;
            if (!email || !code) {
                setStatus("Invalid verification link")
                setVerified(false);
                return;
            }

            if (resetpass) {
                console.info("Reset pass enabled")
                setEnablePasswordReset(true);
            } else {
                console.info("Reset pass not enabled")
                setEnablePasswordReset(false);
            }

            setEmail(email);
            const requestDetail = { "email": email, "verification_code": code} as EmailVerificationRequest;
            try {
                const response = await verifyEmail(requestDetail);
                if (response.success) {
                    if (!resetpass) {
                        setStatus("Success!")
                    }
                    setVerified(true);

                } else {
                    setStatus("Email verification failed.")
                    setVerified(false);
                }
                return;
            } catch (error) {
                console.error("Email verify failed")
            }
            setStatus("Email verification failed.")
            setVerified(false);
        }

        doVerify();

    }, [searchParams])

    return (
        <JunkyardCardContainer {...props}>
            <JunkyardCard variant="outlined">
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                    {(verified && enablePasswordReset && !passwordResetSuccess) && (
                        <Box
                            component="form"
                            onSubmit={submitPasswordReset}
                            noValidate
                        >
                            <Stack spacing={2} direction="column">
                                <FormControl>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <FormLabel htmlFor="password">Enter a new password</FormLabel>
                                    </Box>
                                    <TextField
                                        name="password"
                                        placeholder="••••••"
                                        type="password"
                                        id="password"
                                        autoComplete="current-password"
                                        autoFocus
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={passwordError ? 'error' : 'primary'}
                                    />
                                    <TextField
                                        error={passwordError}
                                        helperText={passwordErrorMessage}
                                        name="passwordDup"
                                        placeholder="••••••"
                                        type="password"
                                        id="passwordDup"
                                        autoComplete="current-password"
                                        autoFocus
                                        required
                                        fullWidth
                                        variant="outlined"
                                        color={passwordError ? 'error' : 'primary'}
                                    />
                                </FormControl>
                                <Button type={"submit"} fullWidth variant="contained">
                                   Reset Password
                                </Button>
                            </Stack>
                        </Box>

                    )}
                    {(verified && (!enablePasswordReset || (enablePasswordReset && passwordResetSuccess))) && (
                        <Box sx={{flexDirection: 'column',
                            justifyContent: "center",
                            display: "flex",
                            alignItems: "center"
                        }}>
                            <Typography variant="h5">
                                Success!
                            </Typography>
                            <Button type="submit" fullWidth variant="contained" onClick={navigateToLogin}
                                    sx={{ marginTop: 2}}
                            >
                                Go to login page
                            </Button>
                        </Box>

                    )}
                    {!verified &&
                    (
                        <Typography variant="h5">
                            {status}
                        </Typography>
                    )}
                </Box>
            </JunkyardCard>
        </JunkyardCardContainer>
    );
};
